import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Image} from '../../../admin/artisticobjects/types/Image';
import {getImageUrl, getThumbnailUrl} from '../../utils/imageUtils';
import ZoomablePhoto from './ZoomablePhoto';
import {Tooltip} from 'primereact/tooltip';

interface DetailsImageSectionProps {
  mainImage?: Image;
  thumbnailImages: Image[];
  onThumbnailClick: (image: Image) => void;
  altTextPrefix: string;
  placeholderAlt?: string;
  children?: React.ReactNode;
}

const DetailsImageSection: React.FC<DetailsImageSectionProps> = ({
                                                                   mainImage,
                                                                   thumbnailImages,
                                                                   onThumbnailClick,
                                                                   altTextPrefix,

                                                                   placeholderAlt,
                                                                   children
                                                                 }) => {
  const {t} = useTranslation();
  const actualPlaceholderAlt = placeholderAlt || t('common.placeholder');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const getCopyrightText = (): string => {
    if (!mainImage) {
      return "<i class='fa-regular fa-copyright'></i> CVP";
    }

    const copyrights = mainImage.copyrights?.trim() || 'CVP';
    return `<i class='fa-regular fa-copyright'></i> ${copyrights}`;
  };

  const tooltipContent = <div dangerouslySetInnerHTML={{__html: getCopyrightText()}}/>;

  return (
    <div id="image-section" className="lg:col-span-2">
      <div id="main-image-container" className="bg-black shadow-sm overflow-hidden mb-2">
        <div className="relative flex items-center justify-center min-h-[400px]">
          <ZoomablePhoto
            src={mainImage ? getImageUrl(mainImage.path) : '/images/placeholder.jpg'}
            alt={mainImage?.originalName || actualPlaceholderAlt}
            isExpanded={isExpanded}
            onToggleExpand={handleToggleExpand}
            className={isExpanded ? 'w-full h-full object-cover' : 'max-h-[85vh] w-auto object-contain'}
          />
          <i
            id="copyright-icon"
            className="fa fa-copyright absolute bottom-4 left-4 text-white text-l cursor-pointer"
          >
          </i>
          <Tooltip
            target="#copyright-icon"
            pt={{
              text: {className: 'bg-gray-600 text-white text-sm'}
            }}>
            {tooltipContent}
          </Tooltip>
        </div>
      </div>

      {children}

      {thumbnailImages.length > 0 && (
        <div id="thumbnails-section" className="bg-white shadow-sm p-4">
          <h3 className="text-sm font-medium text-stone-900 mb-3">{t('details.sections.additionalViews')}</h3>
          <div className="grid grid-cols-6 gap-3">
            {thumbnailImages.map((thumbnailImage) => (
              <div
                key={thumbnailImage.id}
                className="aspect-square bg-stone-100 overflow-hidden cursor-pointer hover:ring-2 hover:ring-stone-300 transition-all"
                onClick={() => onThumbnailClick(thumbnailImage)}
              >
                <img
                  className="w-full h-full object-cover"
                  src={getThumbnailUrl(thumbnailImage)}
                  alt={thumbnailImage.originalName || t('details.alt.additionalView', {prefix: altTextPrefix})}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailsImageSection;
